// src/hooks/useTickets.ts
//
// React data hook for one ticket view (My tickets / All open / Closed). Plain
// useState/useCallback/useEffect per the repo's hook style (useRegions) — no
// React Query. All data access goes through ticketsService; this story has no
// mutations at all (reads only).
//
// Live updates: a postgres_changes subscription on yak_tickets drives a SILENT
// refetch of the current page+filters — no loading flash, no focus stealing.
// One yak_tickets subscription covers both state and assignment changes: every
// transition that reassigns updates yak_tickets.assignee_user_id in the same
// transaction (3.5's materialized-assignee contract), so ticket UPDATE events
// carry both.
//
// Silent-refresh non-contradiction (copied from useRegions): only the
// initial/explicit load owns the error banner. A failed background refetch
// leaves the last-known page in place and only logs — surfacing it would
// contradict a page that is already showing valid data.

import { useState, useEffect, useCallback, useRef } from 'react'
import { ticketsService } from '../services/ticketsService'
import { useRealtimeRefetch } from './useRealtimeRefetch'
import type {
  YakTicketWithAssignee,
  YakTicketViewKey,
  YakTicketFilters,
} from '../types/yak'

interface UseTicketsReturn {
  tickets: YakTicketWithAssignee[]
  total: number
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useTickets(
  view: YakTicketViewKey,
  filters: YakTicketFilters,
  page: number
): UseTicketsReturn {
  const [tickets, setTickets] = useState<YakTicketWithAssignee[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Latest view/filters/page in a ref so the realtime callback (stable
  // identity) always refetches the CURRENT query without re-subscribing on
  // every keystroke/page change.
  const argsRef = useRef({ view, filters, page })
  argsRef.current = { view, filters, page }

  const fetchPage = useCallback(async (silent = false) => {
    const { view, filters, page } = argsRef.current
    try {
      if (!silent) setLoading(true)
      setError(null)
      const result = await ticketsService.listTickets(view, filters, page)
      setTickets(result.tickets)
      setTotal(result.total)
    } catch (err) {
      if (!silent) {
        setError(err instanceof Error ? err.message : 'Failed to load tickets')
      } else {
        console.error('Ticket list refresh failed', err)
      }
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  // Re-run the visible (non-silent) load whenever the query inputs change.
  useEffect(() => {
    fetchPage()
  }, [view, filters, page, fetchPage])

  const silentRefetch = useCallback(() => {
    fetchPage(true)
  }, [fetchPage])
  useRealtimeRefetch('yak_tickets', silentRefetch)

  return { tickets, total, loading, error, refetch: () => fetchPage() }
}
